/* global Sys, Core, Services, Resources, Utils, Platform */
Sys.ns("Core.Slots");

/**
 * The bonus program widget controller.
 *
 * @class Core.Slots.BonusProgramWidgetController
 * @extends Core.Controller
 */
Core.Slots.BonusProgramWidgetController = {
    constructor: function() {
        Core.Slots.BonusProgramWidgetController.superclass.constructor.apply(this, arguments);
    },

    /**
     * Setup event listeners.
     *
     * @private
     * @returns {void}
     */
    setupEvents: function() {
        this.on({
            "notify:stateHandler.enteringBonusProgramWidgetState": this.initBonusProgramWidget,
            "notify:scaling.gameSizeChanged": this.onGameSizeChanged,
            "notify:bonusProgramWidget.notification": this.notificationHandler,
            "notify:bonusProgramWidget.close": this.closeBonusProgramWidget,
            "notify:responseParser.gameConfigurationParsed": this.checkForAdditionalFreeRounds,
            "notify:responseParser.bonusProgramWidgetValuesParsed": this.onBonusProgramWidgetValuesParsed,
            "notify:bonusProgramWidget.reInit": this.reInit,
            "notify:settingsManager.settingChanged": this.onSettingChanged,

            "view:orientationChanged": this.notifyOrientation,
            "notify:viewport.scaled": this.viewportScaled
        });
    },

    /**
     * Addresses the Chrome-IOS race condition for calculating and setting height.
     *
     * @returns {void}
     */
    viewportScaled: function() {
        var me = this;

        if (Platform.isChromeForIOS) {
            window.setTimeout(function() {
                me.view.refresh();
                me.notifyOrientation(Utils.Platform.getOrientation());
                me.onGameSizeChanged();
            }, 500);
        }
    },

    /**
     * Initialises the widget and requests widget variant, then either starts or closes the bonus program.
     *
     * @protected
     * @returns {void}
     */
    initBonusProgramWidget: function() {
        var me = this,
            widgetUrl = this.model.readData("widgetUrl"),
            widgetId = this.model.readData("bonusProgramWidgetGameId"),
            gameId = Resources.readData("queryData").gameId,
            timeout = this.model.readData("slowServerRequestTimeLimit"),
            theme;

        this.view.initViewData();

        // Split out optional theme from widgetId.
        if (widgetId.indexOf("##") !== -1) {
            theme = widgetId.split("##")[1];
            widgetId = widgetId.split("##")[0];
            this.model.storeData("bonusProgramWidgetTheme", theme);
        }

        this.disableBasicGamePanelButtons();

        this.fetchWidgetVariants(widgetUrl, widgetId, timeout, function(response) {
            var widgetPath;

            if (!response) {
                me.closeBonusProgramWidget();
                return;
            }
            widgetPath = me.parseVariantsResponse(response, widgetUrl, widgetId, theme, gameId);

            me.startBonusProgramWidget(widgetPath);
        });
    },

    /**
     * Fetches variants.json file, if success it returns the response, if fail or timeout it returns false.
     *
     * @protected
     * @param {string} widgetUrl The location of the widgets.
     * @param {string} widgetId The id of the widget.
     * @param {int} timeout How long the fetching of the variants.json file is allowed to take before it times out, in milliseconds.
     * @param {Function} cb Callback to call after fetching of variants.json file is done or failed.
     * @returns {void}
     */
    fetchWidgetVariants: function(widgetUrl, widgetId, timeout, cb) {
        var widgetLocation = this.getWidgetLocationByWidgetID(widgetId),
            request = Sys.utils.httpGet({ url: widgetUrl + widgetLocation + "/variants.json" }),
            slowRequestTimeout = setTimeout(function() {
                cb(false);
            }, timeout);

        request
            .done(function(response) {
                cb(response);
            })
            .fail(function(response) {
                cb(false);
            })
            .always(function() {
                clearTimeout(slowRequestTimeout);
            });
    },

    /**
     * SPP has different bonusProgram WidgetGameId in different versions.
     * 7.4.x: 'default_w'.
     * 7.5.x: 'game_branded_w'.
     * > 7.6.x: 'free_rounds_widget'.
     *
     * @param {string} widgetId The id of the widget.
     * @returns {string} The 'fixed' id of the widget.
     */
    getWidgetLocationByWidgetID: function(widgetId) {
        return widgetId === "default_w" || widgetId === "game_branded_w" ? "free_rounds_widget" : widgetId;
    },

    /**
     * Tries to parse the variants.json file, if it succeeds it returns the widget path for the chosen widget, otherwise it returns false.
     *
     * @protected
     * @param {Object} response Response object from the variants.json fetching.
     * @param {string} widgetUrl The location of the widgets.
     * @param {string} widgetId The id of the widget.
     * @param {string} theme The theme of the widget.
     * @param {string} gameId The id of the game.
     * @returns {string|boolean} The widget path for the chosen widget, false otherwise.
     */
    parseVariantsResponse: function(response, widgetUrl, widgetId, theme, gameId) {
        var variants,
            themeOrGame;

        try {
            variants = JSON.parse(response.response);
            themeOrGame = theme ? variants[theme] : variants[gameId];
            return widgetUrl + this.getWidgetLocationByWidgetID(widgetId) + (themeOrGame || variants["default"]);
        }
        catch (e) {
            return false;
        }
    },

    /**
     * Starts the widget.
     *
     * @protected
     * @param {string} widgetPath The path to the widget that should be loaded in the iframe.
     * @returns {void}
     */
    startBonusProgramWidget: function(widgetPath) {
        if (widgetPath) {
            this.view.loadIframePage(widgetPath);
            Resources.storeData("bonusProgramWidgetActive", true);
        }
        else {
            this.closeBonusProgramWidget();
        }
    },

    /**
     * Re-initialises the widget.
     *
     * @protected
     * @param {Object} parsedResponse The response object.
     * @returns {void}
     */
    reInit: function(parsedResponse) {
        this.model.setBonusProgramData(parsedResponse);

        if (this.model.bonusProgramWidgetEnabled()) {
            this.initBonusProgramWidget();
        }
    },

    /**
     * Sends data to widget iframe.
     *
     * @protected
     * @param {string} action The action being performed by the widget.
     * @param {Object} [data] The data to send.
     * @param {string} [dataRoute] An identifier for which method should handle the returned data.
     * @returns {void}
     */
    sendMessage: function(action, data, dataRoute) {
        var iframe = document.getElementById("bonusProgramWidgetIframe");

        if (iframe) {
            iframe.contentWindow.postMessage({
                moduleId: "bonusProgramWidget",
                action: action,
                data: data || null,
                dataRoute: dataRoute || null
            }, "*");
        }
    },

    /**
     * Handles notifications from the widget page.
     *
     * @protected
     * @param {Object|string} notification The notification received.
     * @returns {void}
     */
    notificationHandler: function(notification) {
        var language,
            specifiedLanguage,
            queryData;

        switch (notification.action) {
            // Should we add one for informing of errors?
            case "widgetReady":
                this.notifyOrientation(Utils.Platform.getOrientation());

                language = Resources.readData("language");
                queryData = Resources.readData("queryData");
                specifiedLanguage = language.lang || language.defaultLang;
                this.notifyLanguage(specifiedLanguage);

                this.sendMessage("soundSettingChange", Services.gameSettings.getSetting("volume"));

                this.sendMessage("defaultValues", {
                    operatorId: queryData.operatorId,
                    gameId: queryData.gameId,
                    theme: this.model.readData("bonusProgramWidgetTheme")
                });
                break;
            case "getTextStrings":
                this.getTextStrings(notification.data, notification.dataRoute);
                break;
            case "roundStarted":
                this.roundStarted();
                break;
            case "resumeNormalGameplay":
                this.resumeNormalGameplay();
                break;
            case "playFreeRoundsNow":
                this.playFreeRoundsNow();
                break;
            case "getSectionValues":
                this.sendMessage("populateSectionValues", this.model.readData("bonusProgramWidgetSections"));
                break;
            default:
                this.sendMessage("actionNotSupported");
        }
    },

    /**
     * Retrieves the text strings requested by the widget iframe.
     *
     * @protected
     * @param {Object} textMap The text string keys.
     * @param {string} dataRoute An identifier for which method should handle the returned data.
     * @returns {Object} The text string values.
     */
    getTextStrings: function(textMap, dataRoute) {
        var textTmp,
            texts = {};

        Sys.iterate(textMap, function(key, values) {
            if (Sys.isArray(values) && values.length > 0) {
                textTmp = Services.languageManager.getText(key, values);
            }
            else {
                textTmp = Services.languageManager.getText(key);
            }

            if (textTmp) {
                texts[key] = textTmp;
            }
        });

        this.sendMessage("populateTextStrings", texts, dataRoute);
    },

    /**
     * Triggered when widget has notified that the play has been initiated.
     *
     * @protected
     * @returns {void}
     */
    roundStarted: function() {
        this.requestWidgetAction();
    },

    /**
     * Sends win outcome request to SPP.
     *
     * @protected
     * @returns {void}
     */
    requestWidgetAction: function() {
        // "widget" is a mock action to trigger standalone platform (mocked responses)
        // freeroundwidgetspin should be renamed to freeroundwidgetround - tell SPP.
        // maybe not "freeround"... "award"? "bonus"?
        this.fireEvent("request:serverManager.sendAction", "widgetspin", "notify:responseParser.bonusProgramWidgetValuesParsed");
    },

    /**
     * Checking if additional free rounds is given. If a widget is not rewarded, fire the wait event.
     *
     * @private
     * @param {Object} parsedResponse Parsed server response containining game information.
     * @returns {void}
     */
    checkForAdditionalFreeRounds: function(parsedResponse) {
        if (!parsedResponse.freeRoundWidgetEnabled) {
            this.fireEvent("notify:bonusProgramWidget.closed");
        }
    },

    /**
     * Sends win outcome to the widget.
     *
     * @protected
     * @param {Object} e The server response object.
     * @returns {void}
     */
    onBonusProgramWidgetValuesParsed: function(e) {
        this.sendMessage("processRoundOutcome", {
            indexToShow: e.indexToShow
        });
    },

    /**
     * Handles play free rounds later widget button action.
     *
     * @protected
     * @returns {void}
     */
    resumeNormalGameplay: function() {
        this.closeBonusProgramWidget();
    },

    /**
     * Handles play free rounds now widget button action.
     *
     * @protected
     * @returns {void}
     */
    playFreeRoundsNow: function() {
        this.closeBonusProgramWidget();
        this.fireEvent("request:freeRounds.requestGameConfiguration");
    },

    /**
     * Closes the widget.
     *
     * @protected
     * @returns {void}
     */
    closeBonusProgramWidget: function() {
        this.view.closeIframePage();
        this.fireEvent("notify:bonusProgramWidget.closed");
        this.enableBasicGamePanelButtons();
    },

    /**
     * Enables settings menu.
     *
     * @protected
     * @returns {void}
     */
    enableBasicGamePanelButtons: function() {
        this.fireEvent("request:settingsButton.enable");
        this.fireEvent("request:enableBasicGamePanel", this.MODULE_NAME);
    },

    /**
     * Disables settings menu.
     *
     * @protected
     * @returns {void}
     */
    disableBasicGamePanelButtons: function() {
        this.fireEvent("request:settingsButton.disable");
        this.fireEvent("request:disableBasicGamePanel", this.MODULE_NAME);
    },

    /**
     * Notifies the new orientation to the widget.
     *
     * @protected
     * @param {string} orientation The updated orientation.
     * @returns {void}
     */
    notifyOrientation: function(orientation) {
        this.sendMessage("updateOrientation", {
            orientation: orientation
        });
    },

    /**
     * Notifies detected language to the widget.
     *
     * @protected
     * @param {string} lang The language.
     * @returns {void}
     */
    notifyLanguage: function(lang) {
        this.sendMessage("updateLanguage", {
            lang: lang
        });
    },

    /**
     * Refreshes the view when the game size has changed.
     *
     * @protected
     * @returns {void}
     */
    onGameSizeChanged: function() {
        var requestAnimationFrameCB;

        if (typeof this.view.refresh === "function") {
            this.model.storeData("gameSizeChangedTriggered", true);
            this.view.refresh();

            requestAnimationFrameCB = function() {
                this.notifyOrientation(Utils.Platform.getOrientation());
            };

            window.requestAnimationFrame(requestAnimationFrameCB.bind(this));
        }
    },

    /**
     * Notifies widget about changes to the volume settings.
     *
     * @param {string} setting Name of the setting changed by the user.
     * @param {boolean|number} value The new value of the control.
     * @returns {void}
     */
    onSettingChanged: function(setting, value) {
        if (setting === "volume") {
            this.sendMessage("soundSettingChange", value);
        }
    }
};

Core.Slots.BonusProgramWidgetController = Sys.extend(Core.Controller, Core.Slots.BonusProgramWidgetController, "Core.Slots.BonusProgramWidgetController");
